<template>
    <gi-form
        v-if="form"
        ref="form"
        :form="form"
        :on-submit-fn="onSubmitFn"
        :history="history"
        :hide-actions="hideActions"
        @success="onSuccess"
        @error="onError"
        @form-error="onFormError"
        @load-history="onLoadHistory"
        @start-history-autosave="startHistoryAutosave"
        @stop-history-autosave="stopHistoryAutosave"
    >
        <template v-if="!hideHeader" #header>
            <app-page-header class="mb-4" />
        </template>
        <template #fields="{ loading }">
            <field-string
                v-if="isGrowthInvest()"
                ref="gi_ref"
                v-model="form.gi_ref"
                :disabled="loading"
                :label="$t('gi_ref')"
            />
            <field-string ref="title" v-model="form.title" :disabled="loading" required :label="$t('title')" />
            <field-string ref="name" v-model="form.name" :disabled="loading" required :label="$t('full_name')" />
            <field-email ref="email" v-model="form.email" :disabled="loading" required />
            <field-dob ref="dob" v-model="form.dob" :disabled="loading" required />
            <field-string
                ref="city_of_birth"
                v-model="form.city_of_birth"
                :disabled="loading"
                :label="$t('country_and_place_of_birth')"
            />
            <field-ni-number ref="national_insurance" v-model="form.national_insurance" :disabled="loading" required />
            <field-phone-numbers
                ref="phone_numbers"
                v-model="form.phone_numbers"
                :disabled="loading"
                required
                :label="$t('phone_numbers')"
            />
            <field-addresses
                ref="addresses"
                v-model="form.addresses"
                :label="$t('addresses')"
                :disabled="loading"
                :require-history="36"
                required
            />
            <field-bank-accounts
                ref="bank_accounts"
                v-model="form.bank_accounts"
                :disabled="loading"
                :label="$t('bank_accounts')"
            />
            <field-nationality ref="nationality" v-model="form.nationality" :disabled="loading" required />
            <field-domiciled ref="domiciled" v-model="form.domiciled" :disabled="loading" required />
            <field-user-tax-certificate-destination
                ref="tax_cert_destination"
                v-model="form.tax_cert_location"
                :disabled="loading"
                required
            />
            <field-toggle
                ref="us_person"
                v-model="form.us_person"
                :disabled="loading"
                :label="$t('is_user_usa_person')"
            >
                <template #info>
                    <p class="mb-0">A US citizen is an individual to whom one or more of the following applies:</p>
                    <ul>
                        <li>Dual citizens of the US and another country</li>
                        <li>US citizen even if residing outside the United States</li>
                        <li>US passport holder</li>
                        <li>Born in the US, unless citizenship has been renounced</li>
                        <li>Lawful permanent resident of the US</li>
                        <li>A ‘substantially present’ person as declared by the US tax regulator</li>
                    </ul>
                </template>
            </field-toggle>
            <!-- eslint-disable max-len -->
            <field-toggle
                ref="politically_exposed"
                v-model="form.politically_exposed"
                :disabled="loading"
                :label="$t('is_user_politically_exposed')"
                :info="$t('if_the_applicant_or_a_close_associate_or_family_member_is_a_pep')"
            />
            <!-- eslint-enable max-len -->
            <field-toggle
                ref="can_login"
                v-model="form.can_login"
                :disabled="loading"
                :label="$t('can_login')"
                :info="$t('if_this_is_left_unticked_the_user_will_be_created_without_sending_any_emails')"
            />

            <field-adviser-accounts
                ref="adviser_accounts"
                v-model="form.adviser_accounts"
                :label="$t('adviser_accounts')"
                :disabled="loading"
            />

            <field-enum
                v-if="isGrowthInvest()"
                v-model="form.investor_structure"
                :disabled="loading"
                :enums="InvestorStructureEnum"
                enum-a11y-key="enums.investor_structure"
                :label="$t('investor_structure')"
            />

            <field-investor-category
                ref="category"
                v-model="form.category"
                :disabled="loading"
                :label="$t('category')"
            />
            <field-date
                ref="category_expires_on"
                v-model="form.category_expires_on"
                :disabled="loading"
                :label="$t('enter_the_categorisation_expiration_date')"
                :info="$t('categorisation_expiration_date_warning')"
                :rules="[
                    getValidator('before')(
                        form.category_expires_on,
                        $t('categorisation_expiration_date'),
                        form.max_categorisation_expiry_date
                    )
                ]"
            />
        </template>
        <template v-if="prependCancelButton" #prepend-actions>
            <app-button-cancel @click="onCancel" />
        </template>
    </gi-form>
</template>

<script>
import form from '@/mixins/form';
import history from '@/mixins/history';
import Client from '@/models/Client';
import User from '@/models/User';
import InvestorStructureEnum from '@/enums/investor/structure';

export default {
    name: 'CreateClient',
    components: {
        GiForm: () =>
            import(
                /* webpackChunkName: "forms" */
                '@/components/forms/Form'
            ),
        FieldDob: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Dob'
            ),
        FieldEmail: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Email'
            ),
        FieldNiNumber: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/NiNumber'
            ),
        FieldInvestorCategory: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/InvestorCategory'
            ),
        FieldPhoneNumbers: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/PhoneNumbers'
            ),
        FieldAddresses: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Addresses'
            ),
        FieldBankAccounts: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/BankAccounts'
            ),
        FieldNationality: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Nationality'
            ),
        FieldDomiciled: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Domiciled'
            ),
        FieldUserTaxCertificateDestination: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/UserTaxCertificateDestination'
            ),
        FieldAdviserAccounts: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/AdviserAccounts'
            )
    },
    mixins: [form, history],
    data: () => ({
        id: 'CreateClient',
        model: Client,
        updateCacheWithGetAfterSubmit: false,
        InvestorStructureEnum
    }),
    methods: {
        async getSubmissionDialog() {
            if (this.form && this.form.adviser_accounts && this.form.adviser_accounts.length) {
                const creator = await User.$get(this.current_user_id);

                const creatorHasVisibility = this.form.adviser_accounts.every(adviserAccount => {
                    const primaryContact = adviserAccount.primary_contact;

                    if (!primaryContact) {
                        return true;
                    }

                    const primaryContactVisibilityGroupId = primaryContact.roles.find(
                        role => role.organisation_id === adviserAccount.adviser_id
                    )?.visibility_group_id;

                    if (!primaryContactVisibilityGroupId) {
                        return true;
                    }

                    const creatorVisibilityGroupId = creator.roles.find(
                        role => role.organisation_id === adviserAccount.adviser_id
                    )?.visibility_group_id;

                    if (!creatorVisibilityGroupId) {
                        return true;
                    }

                    return primaryContactVisibilityGroupId === creatorVisibilityGroupId;
                });

                if (!creatorHasVisibility) {
                    return {
                        size: 'large',
                        title: this.$t('visibility_warning'),
                        text:
                            this.$t('your_view_of_clients_is_determined_by_a_different_visibility_group') +
                            ' ' +
                            this.$t('would_you_like_to_add_this_client_to_both'),
                        continueButtonText: this.$t('yes') + ', ' + this.$t('add_to_both'),
                        onContinueFn: () => {
                            this.form.add_to_creators_visibility_group = true;
                            return this.submit({ skipDialog: true });
                        },
                        cancelButtonText: this.$t('no') + ', ' + this.$t('i_dont_want_to_see_this_client'),
                        onCancelFn: () => {
                            this.form.add_to_creators_visibility_group = false;
                            return this.submit({ skipDialog: true });
                        }
                    };
                }
            }
        }
    }
};
</script>
